import { useEffect, useState } from 'react';
import { useColumnContextAPI } from '../../../shared/context/GetAllColumnContext';
import { useUserContextApi } from '../../../shared/context/UserContext';
import { useConfigContext } from '../../../shared/context/ConfigContext';
import GridHeaderAction from '../../../shared/components/grid/GridHeaderAction';
import { ArrowBack, Cached, Upload } from '@airbus/icons/react';
import { MenuItem } from '@airbus/components-react';
import { BackToNPIBoard } from './BackToNPIBoard';
import { useLanguageContextAPI } from '../../../shared/context/LanguageContext';
import styles from './NPIActions.module.css';
import {
  BulkUpdateDialog,
  type BulkAttributeConfig,
  type BulkAttributeValue,
  type BulkUpdateDialogCacheState,
} from '../../../shared/components/bulkUpdate';
import { AttributeGroup } from '../../../shared/types';
import { prepareBulkUpdateDialogData } from '../services/prepareBulkUpdateDialogData';
import { getBulkUpdateNpiSteeringIdsService } from '../services/getBulkUpdateNpiSteeringIdsService';
import { commitBulkUpdateNpiSteeringService } from '../services/commitBulkUpdateNpiSteeringService';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { transformFilterItems } from '../../../shared/utils/transformFilterItems';
import type { GridFilterModel } from '../../../shared/types';

type BulkUpdateSession = {
  attributes: BulkAttributeConfig[];
  groups: AttributeGroup[];
  rowsToUpdate: number;
  isRowLimitExceeded: boolean;
  id1List: number[];
};

export default function NPIActions(props) {
  const { setAlertbar, rowselectionmodel, rows } = props;
  const { t } = useLanguageContextAPI();
  const { userDetails } = useUserContextApi();
  const {
    tableFeatureData,
    accessDetails: access,
    updatereRender,
    tableDetails,
    allAccessibleColumns,
  } = useColumnContextAPI();
  const { bulkUpdateMaxObjectsSize } = useConfigContext();
  const [isBackToNpiDisabled, setIsBackToNpiDisabled] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkUpdateDialog, setOpenBulkUpdateDialog] = useState(false);
  const [rowSelected, setRowSelected] = useState(null);
  const [keysInAccess, setKeysInAccess] = useState(false);
  const [bulkUpdateSession, setBulkUpdateSession] = useState<BulkUpdateSession | null>(null);
  const [bulkUpdateDialogCache, setBulkUpdateDialogCache] =
    useState<BulkUpdateDialogCacheState | null>(null);
  const bulkUpdateUiEnabled = useFeatureFlagEnabled('npiBulkUpdateUiFlag');

  useEffect(() => {
    if (Object.keys(tableFeatureData).length) {
      setKeysInAccess(Object.keys(tableFeatureData).some((key) => access[key]));
      if (rowselectionmodel.length != 1) {
        setIsBackToNpiDisabled(false);
      } else {
        const [rowId] = rowselectionmodel;
        const row = rowId && rows.find((row) => row.id === rowId);
        if (!row?.mob || row?.mob === 'MoB' || row?.mob === 'Rejected by ME/AV') {
          setIsBackToNpiDisabled(true);
          return;
        } else {
          setIsBackToNpiDisabled(false);
        }
      }
    }
  }, [rowselectionmodel, tableFeatureData]);

  const menuItemClickHandler = (value) => {
    rowSelectionMsg({ value, rowselectionmodel, setAlertbar, rows });
  };

  const handleBulkUpdateClick = async () => {
    const prepared = prepareBulkUpdateDialogData(allAccessibleColumns);

    const screenGridId = tableDetails?.id;
    const schema = tableDetails?.mbdDataGrid?.schemaName;
    const table = tableDetails?.mbdDataGrid?.dataGridInternalName;
    const rawFilterModel = props?.apiRef?.current?.state?.filter?.filterModel;
    const safeFilterModel = transformFilterItems(rawFilterModel, allAccessibleColumns);
    const idsResult = await getBulkUpdateNpiSteeringIdsService({
      screenGridId,
      schema,
      table,
      filterModel: safeFilterModel as unknown as GridFilterModel,
    });

    if (idsResult?.errors?.length) {
      setAlertbar({
        children: idsResult.errors?.[0]?.message ?? t('bulkUpdate.fetchIdsFailed'),
        severity: 'error',
      });
      return;
    }

    const id1List = idsResult?.id1List ?? [];

    if (!id1List.length) {
      setAlertbar({
        children: t('bulkUpdate.noFilteredRows'),
        severity: 'warning',
      });
      return;
    }

    const isRowLimitExceeded = id1List.length > bulkUpdateMaxObjectsSize;

    if (isRowLimitExceeded) {
      setAlertbar({
        children: t('bulkUpdate.limitExceededWarning', {
          count: id1List.length,
          limit: bulkUpdateMaxObjectsSize,
        }),
        severity: 'warning',
      });
    }

    setBulkUpdateSession({
      attributes: prepared.attributes,
      groups: prepared.attributeGroups,
      rowsToUpdate: id1List.length,
      isRowLimitExceeded,
      id1List,
    });

    setOpenBulkUpdateDialog(true);
  };

  const handleBulkUpdateSave = async (
    updates: Array<{ attrInternalName: string; value: BulkAttributeValue }>,
  ) => {
    if (!bulkUpdateSession?.id1List.length) {
      setAlertbar({
        children: t('bulkUpdate.noFilteredRows'),
        severity: 'warning',
      });
      return;
    }

    const commitResult = await commitBulkUpdateNpiSteeringService({
      screenGridId: tableDetails.id,
      schemaName: tableDetails?.mbdDataGrid?.schemaName,
      id1List: bulkUpdateSession.id1List,
      updates,
      attributes: bulkUpdateSession.attributes,
      updatedBy: userDetails?.userId,
      bulkUpdateMaxObjectsSize,
    });

    if (!commitResult.success) {
      setAlertbar({
        children: commitResult.errors?.[0]?.message ?? t('bulkUpdate.saveFailed'),
        severity: 'error',
      });
      return;
    }

    setAlertbar({
      children: t('bulkUpdate.saveSuccess'),
      severity: 'success',
    });

    updatereRender();

    setBulkUpdateDialogCache(null);
    setOpenBulkUpdateDialog(false);
  };

  const rowSelectionMsg = ({ value, rowselectionmodel, setAlertbar, rows }) => {
    if (!rowselectionmodel.length || rowselectionmodel.length > 1) {
      const errorMessage =
        rowselectionmodel.length === 0 ? t('please_select_row') : t('please_select_single_row');
      setAlertbar({
        children: errorMessage,
        severity: 'warning',
      });
      return;
    }
    const selectedRow = rows.find((row) => row.id === rowselectionmodel[0]);
    setRowSelected(selectedRow);
    const finalNpiHtz =
      value.featureName === 'show_last_htz'
        ? selectedRow?.htz ?? ''
        : selectedRow?.htz?.slice(0, 10) ?? '';
    if (value.featureName === 'back_to_npi_board') {
      setOpenDialog(true);
    } else
      window.open(
        `http://plmprod.de.pa.corp/Windchill/netmarkets/jsp/ext/pag/Go2LatestWtPart.jsp?number=${finalNpiHtz}`,
      );
  };
  return (
    <>
      {keysInAccess && (
        <GridHeaderAction>
          {Object.entries(tableFeatureData).map(
            ([key, value]) =>
              key != 'action' &&
              key !== 'bulk_update' &&
              access[key] && (
                <MenuItem
                  className={styles.menuItem}
                  key={key}
                  onClick={() => menuItemClickHandler(value)}
                  icon={
                    key !== 'back_to_npi_board' ? (
                      <Cached className={styles.icon} />
                    ) : (
                      <ArrowBack className={styles.icon} />
                    )
                  }
                  disabled={!!(key === 'back_to_npi_board' && isBackToNpiDisabled)}
                >
                  {tableFeatureData[key][`featureName`]}
                </MenuItem>
              ),
          )}

          {bulkUpdateUiEnabled && access['bulk_update'] && (
            <MenuItem
              className={styles.menuItem}
              key='bulk_update'
              onClick={handleBulkUpdateClick}
              icon={<Upload className={styles.icon} />}
            >
              {t('header_actions.bulk_update')}
            </MenuItem>
          )}
        </GridHeaderAction>
      )}
      <BackToNPIBoard
        setAlertbar={setAlertbar}
        updatereRender={updatereRender}
        rowSelected={rowSelected}
        open={openDialog}
        setOpen={setOpenDialog}
        tableDetails={tableDetails}
      />

      <BulkUpdateDialog
        open={openBulkUpdateDialog}
        onClose={() => setOpenBulkUpdateDialog(false)}
        onSave={handleBulkUpdateSave}
        rowsToUpdate={bulkUpdateSession?.rowsToUpdate ?? 0}
        rowLimitExceeded={bulkUpdateSession?.isRowLimitExceeded ?? false}
        maxRowsToUpdate={bulkUpdateMaxObjectsSize}
        cachedState={bulkUpdateDialogCache}
        onStateCacheChange={setBulkUpdateDialogCache}
        attributes={bulkUpdateSession?.attributes ?? []}
        attributeGroups={bulkUpdateSession?.groups ?? []}
        title={t('bulkUpdate.title')}
      />
    </>
  );
}
